import { Component, ElementRef, EventEmitter, Input, NgModule, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingAttributeComponent, ListLoadingIndicatorComponent } from '@shared/ui';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

export interface ResourceListItem {
  id: string;
  name: string;
  type?: string;
  description?: string;
  nestedResourcesCount?: number;
  assigned?: boolean;
  /**
   * True for staged rows that exist only client-side (not yet persisted)
   */
  pending?: boolean;
}

export enum ResourceColumn {
  TYPE = 'type',
  NAME = 'name',
  NESTED_RESOURCES = 'nestedResources',
  EDIT = 'edit',
  DELETE = 'delete',
  ASSIGNED = 'assigned',
}

@Component({
    selector: 'ts-resources-list',
    templateUrl: './resources-list.component.html',
    styleUrls: ['./resources-list.component.scss'],
    standalone: false
})
export class ResourcesListComponent implements OnChanges {
  @Input() items: ResourceListItem[] = [];
  @Input() loading = false;
  @Input() noResources = false;
  @Input() emptyMessageKey = 'info.no_resources';
  @Input() columns: ResourceColumn[] = [
    ResourceColumn.TYPE,
    ResourceColumn.NAME,
    ResourceColumn.NESTED_RESOURCES,
    ResourceColumn.EDIT,
    ResourceColumn.DELETE,
  ];

  @Output() editClicked = new EventEmitter<ResourceListItem>();
  @Output() deleteClicked = new EventEmitter<ResourceListItem>();
  @Output() assignedToggled = new EventEmitter<ResourceListItem>();

  // Store the container height when loading starts to prevent layout jumps
  public containerHeight: number | null = null;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When loading starts, capture the current height
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    // When loading ends, clear the fixed height
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }

  get visibleColumns(): string[] {
    return this.columns.map((col) => col.valueOf());
  }
}

@NgModule({
  declarations: [ResourcesListComponent],
  exports: [ResourcesListComponent],
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LoadingAttributeComponent,
    ListLoadingIndicatorComponent,
  ],
})
export class ResourcesListComponentModule {}
